// tslint:disable: no-magic-numbers
import React from 'react';
import { Grid, withStyles } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import styles from './Dashboard.styles';
import IDashboardLayoutProperties from './Dashboard.properties';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import { BACK_BUTTON } from 'components/common/ActionBar/constants';
import AddWidgetIcon from 'assets/AddWidgets';
import { useFetchEnquiryProgramDashboardWidgets } from 'api/dashboardMaintenanceEnquiry/dashboardMaintenanceEnquiry';
import AlertWidget from 'components/DashboardMaintenanceEnquiry/Widgets/AlertWidget';
import FieldsWidget from 'components/DashboardMaintenanceEnquiry/Widgets/FieldsWidget';
import GridWidget from 'components/DashboardMaintenanceEnquiry/Widgets/GridWidget';
import { WIDGET_TYPES } from './Dashboard.constants';
import QuickLinksWidget from 'components/DashboardMaintenanceEnquiry/Widgets/QuickLinksWidget/QuickLinksWidget';
import Scrollbars from 'react-custom-scrollbars';
import { withRouter } from 'react-router';
import { CircularProgressSize } from 'utils/constants';
import { GridSize } from '@material-ui/core/Grid';

// -----------------------------
// CARD COMPONENT
// -----------------------------

const Card = (props: any) => {
  const { classes, text, card, programId } = props;

  const widgetTypeInfo = WIDGET_TYPES[card.WidgetId] || {
    widget: 'unknown',
    size: 'Portrait'
  };

  // Render actual widget based on type
  const renderWidget = () => {
    switch (widgetTypeInfo.widget) {
      case 'grid':
        return (
          <div className={classes.widgetContent}>
            <GridWidget
              columns={card?.DashboardWidgetFields.sort((a, b) => a.Sequence - b.Sequence)
                .map((field) => {

                  return {
                    dataField: field.Name?.split(' ').join(''),
                    caption: field.Label,
                    dataType: 'string',
                    width: 150,
                    ...field
                  };
                })}
              maxHeight={500}
              showHeading={true}
              headingText={text}
              card={card}
              programId={programId}
            />
          </div>
        );
      case 'fields':
        return (
          <div className={classes.widgetContent}>
            <FieldsWidget schema={card} />
          </div>
        );
      case 'alert':
        return (
          <div className={classes.widgetContent}>
            <AlertWidget schema={card} />
          </div>
        );
      default:
        return (
          <div className={classes.widgetContent}>
            <h4>Unknown Widget: {text}</h4>
            <div>Type: {widgetTypeInfo.widget}</div>
          </div>
        );
    }
  };

  return (
    <>
      {renderWidget()}
    </>
  );
};

// -----------------------------
// MAIN COMPONENT
// -----------------------------

const DashboardComponent = (props: IDashboardLayoutProperties) => {
  const { classes, programId, changeSelectedTab, changeSelectedRecord } = props;
  const params = new URLSearchParams(location.search);
  const [dashboardWidgets, setDashboardWidgets] = React.useState<any>({});
  const { setActionBar } = React.useContext(ActionBarContext);
  const enquiryProgramDashboardWidgetsMutation = useFetchEnquiryProgramDashboardWidgets();

  React.useEffect(
    () => {
      if (programId && (params.get('CustomerId') || params.get('ProductId'))) {
        fetchEnquiryProgramDashboardWidgets();
      }
    },
    [programId]
  );

  const fetchEnquiryProgramDashboardWidgets = async () => {
    const data = await enquiryProgramDashboardWidgetsMutation.mutateAsync({
      urlQueryParams: {
        programId: programId
      }
    });
    setDashboardWidgets(data ?? {});
  };

  const [columns, setColumns] = React.useState({
    left: [] as any[],
    right: [] as any[]
  });

  React.useEffect(
    () => {
      changeSelectedRecord(dashboardWidgets?.Dashboard?.DashboardId?.Value);
    },
    [dashboardWidgets?.Dashboard?.DashboardId?.Value]
  );
  React.useEffect(
    () => {
      changeSelectedTab('Dashboard');
    },
    []
  );

  React.useEffect(
    () => {
      if (dashboardWidgets?.Dashboard?.DashWidgets) {
        const widgets = dashboardWidgets.Dashboard.DashWidgets;

        const mappedCards = widgets.map((widget: any) => ({
          ...widget,
          id: widget.DashboardWidgetId.toString(),
          text: widget.WidgetTitle,
          widgetType: WIDGET_TYPES[widget.WidgetId] || {
            widget: 'unknown',
            size: 'Portrait'
          }
        }));

        const left = mappedCards
          .filter((w) => w.LayoutPanel === 'M')
          .sort((a, b) => a.Sequence - b.Sequence);

        const right = mappedCards
          .filter((w) => w.LayoutPanel === 'R')
          .sort((a, b) => a.Sequence - b.Sequence);

        setColumns({ left, right });
      }
    },
    [dashboardWidgets]
  );

  React.useEffect(
    () => {
      setActionBar({
        leftIcons: [BACK_BUTTON],
        centerIcons: [
          {
            Icon: AddWidgetIcon,
            label: 'QuickLinks',
            action: null,
            disabled: false,
            renderComponent: () => (
              <QuickLinksWidget />
            ),
          }
        ],
        rightIcons: [],
      });
    },
    []
  );

  // Calculate grid sizes based on widget types
  const getGridSize = (card: any) => {
    const widgetInfo = WIDGET_TYPES[card.WidgetId] || { size: 'Portrait' };

    if (widgetInfo.size === 'Landscape') {

      return {
        xs: 12 as GridSize,
        sm: 12 as GridSize,
        md: 12 as GridSize
      };
    }

    // Portrait widgets: 3 per row on large, 2 per row on medium, 1 per row on small
    return {
      sm: 12 as GridSize,
      md: 6 as GridSize,
      lg: columns.right.length > 0 ? 6 : 4 as GridSize,
    };
  };

  if (enquiryProgramDashboardWidgetsMutation?.isLoading) {

    return (
      <div style={{
        position: 'absolute',
        top: '50%',
        left: 'calc(50%)',
      }}>
        <CircularProgress
          size={CircularProgressSize}
          color={'secondary'}
          variant={'indeterminate'}
        />
      </div>
    );
  }

  return (
    <div className={classes.formContainer}>
      <Scrollbars className={classes.scrollbarContainer}>
        <Grid container spacing={8} style={{ height: '100%' }}>
          <Grid item xs={columns.right.length > 0 ? 8 : 12} className={columns.right.length ? '' : classes.leftColumnContainer}>
            <div className={classes.leftColumn}>
              <Grid container spacing={8}>
                {columns.left.map((card) => {
                  const widgetTypeInfo = WIDGET_TYPES[card.WidgetId] || {
                    widget: 'unknown',
                    size: 'Portrait'
                  };

                  return (
                    <Grid
                      key={card.id}
                      item
                      {...getGridSize(card)}
                      className={widgetTypeInfo.size === 'Landscape' ?
                        classes.fullWidthCard :
                        'classes.standardCard'
                      }
                    >
                      <Card
                        card={card}
                        id={card.id}
                        text={card.text || ''}
                        classes={classes}
                        programId={programId}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </div>
          </Grid>

          {columns.right.length > 0 && (
            <Grid item xs={4}>
              <div className={classes.rightColumn}>
                {columns.right.map((card) => {
                  const widgetTypeInfo = WIDGET_TYPES[card.WidgetId] || {
                    widget: 'unknown',
                    size: 'Portrait'
                  };

                  return (
                    <div
                      key={card.id}
                      className={widgetTypeInfo.size === 'Landscape' ?
                        classes.fullWidthCard :
                        'classes.standardCard'
                      }
                    >
                      <Card
                        card={card}
                        id={card.id}
                        text={card.text || ''}
                        classes={classes}
                        programId={programId}
                      />
                    </div>
                  );
                })}
              </div>
            </Grid>
          )}
        </Grid>
      </Scrollbars>
    </div>
  );
};

export default withRouter(withStyles(styles, { index: 1 })(DashboardComponent));
